import { ConditionPredicate as Comparator } from '@pepperhq/sdk';
import { AwardTemplate } from 'components/customers/models/Award';
import { Perk } from 'components/loyalty/models/PerkModel';
import { isArray, isArrayOf, isNumber, isString } from 'lib/typeguards';
import { TimeConditionKey, TimeOfDay } from '../models/CampaignTriggerCondition';
import { CampaignTrigger } from './CampaignTrigger';
import {
    CampaignTriggerCondition,
    campaignTriggerConditionLabel,
    ConditionField,
    ConditionVariant,
    isCampaignTriggerCondition,
    isTimeOfDay,
    timeConditionLabel
} from './CampaignTriggerCondition';
import {
    CampaignEffect,
    campaignTriggerEffectLabel,
    isLoyaltyAddAwardEffect,
    isLoyaltyAddAwardEffectStampCard,
    isLoyaltyRemoveAwardEffect,
    isSegmentAddUserEffect
} from './CampaignTriggerEffect';

type ConditionValueKey = TimeConditionKey | string;

function isConditionValueKey(key: any): key is ConditionValueKey {
    return isString(key) || Object.keys(TimeConditionKey).includes(key);
}

export type ConditionValueValue = number | TimeOfDay[] | string | string[];

function isConditionValueValue(value: any): value is ConditionValueValue {
    return (
        isNumber(value) ||
        (isArrayOf(isTimeOfDay, value) && value.length > 0) ||
        isString(value) ||
        isArrayOf(isString, value)
    );
}

export interface IConditionValue {
    key: ConditionValueKey;
    value: ConditionValueValue;
    comparator?: Comparator;
}

function isConditionValue(item: any): item is IConditionValue {
    return !!item && isConditionValueKey(item.key) && isConditionValueValue(item.value);
}

export interface CampaignConditionData {
    type: CampaignTriggerCondition['type'];
    value: IConditionValue[];
}

function isCampaignItemCondition(condition: any): condition is CampaignConditionData {
    return (
        Object.keys(campaignTriggerConditionLabel).includes(condition.type) &&
        isArray(condition.value) &&
        condition.value.length > 0 &&
        isArrayOf(isConditionValue, condition.value)
    );
}

export interface CampaignEffectData {
    type: CampaignEffect;
    target: string;
    points?: string;
}

function isCampaignItemEffect(effect: any, perks: Perk[]): effect is CampaignEffectData {
    return (
        Object.keys(campaignTriggerEffectLabel).includes(effect.type) &&
        !!effect.target &&
        isString(effect.target) &&
        (effect.type === CampaignEffect.ADD_AWARD &&
        perks &&
        perks.find(item => item._id === effect.target).template === AwardTemplate.STAMP_CARD
            ? isString(effect.points) && !!effect.points
            : true)
    );
}

export interface CampaignItem {
    id?: string;
    origin?: CampaignTrigger;
    effect: CampaignEffectData;
    condition: CampaignConditionData;
}

export function isCampaignItem(item: any, perks: Perk[]): item is CampaignItem {
    return (
        !!item &&
        !!item.condition &&
        !!item.effect &&
        isCampaignItemCondition(item.condition) &&
        isCampaignItemEffect(item.effect, perks)
    );
}

export const generateConditionData = (item: CampaignTrigger): CampaignConditionData => {
    if (!item) {
        return null;
    }
    const { condition } = item.data;
    if (isCampaignTriggerCondition(condition)) {
        return {
            type: item.data.condition.type,
            value: Object.entries(condition.data).map(([key, field]) => ({ key, value: field.value }))
        };
    }
    return null;
};

export const generateEffectData = (item: CampaignTrigger): CampaignEffectData => {
    if (!item) {
        return null;
    }
    const { effect } = item.data;
    const effectType = effect.type;
    let effectTarget: string;
    let points: string;
    if (isLoyaltyAddAwardEffect(effect) || isLoyaltyRemoveAwardEffect(effect)) {
        effectTarget = effect.data.perkId;
        if (isLoyaltyAddAwardEffectStampCard(effect)) {
            points = String(effect.data.points);
        }
    }
    if (isSegmentAddUserEffect(effect)) {
        effectTarget = effect.data.segmentId;
    }
    return { points, type: effectType, target: effectTarget };
};

export type CampaignItemConditionRequestData = Record<
    string,
    ConditionField<Comparator, number | string | TimeOfDay[] | string[]>
>;

interface CampaignItemConditionRequest {
    type: string;
    data: CampaignItemConditionRequestData;
}

interface CampaignItemEffectRequestData {
    segmentId?: string;
    perkId?: string;
    points?: number;
    type?: string;
    redemption?: number;
    step?: number;
}

interface CampaignItemEffectRequest {
    type: string;
    data: CampaignItemEffectRequestData;
}

export const generateEffectRequestData = (
    item: CampaignItem,
    perks: Perk[]
): CampaignItemEffectRequestData => {
    const effectData = {} as any;
    if (item.effect.type === CampaignEffect.ADD_SEGMENT_USER) {
        effectData.segmentId = item.effect.target;
    } else {
        effectData.perkId = item.effect.target;
        const perk = perks.find(perk => perk._id === effectData.perkId);
        if (perk.template === AwardTemplate.STAMP_CARD) {
            effectData.points = Number(item.effect.points);
            effectData.type = AwardTemplate.STAMP_CARD;
        }
        if (perk.template === AwardTemplate.PSEUDO_CURRENCY) {
            effectData.redemtion = perk.points.redemption;
            effectData.step = perk.points.step;
            effectData.type = AwardTemplate.PSEUDO_CURRENCY;
        }
    }
    return effectData;
};

export interface CampaignItemRequestData {
    effect: CampaignItemEffectRequest;
    condition: CampaignItemConditionRequest;
}

export const conditionLabels: Record<string, string> = {
    ...timeConditionLabel,
    [ConditionVariant.location]: 'At a particular location',
    [ConditionVariant.audience]: 'A specific audience',
    [ConditionVariant.product]: 'That includes a specific product',
    [ConditionVariant.category]: 'That includes items from a specific category',
    [ConditionVariant.voucher]: 'A specific voucher',
    [ConditionVariant.amount]: 'Of a particular amount'
};

export const amountComparators = [
    Comparator.EQUAL,
    Comparator.GREATER,
    Comparator.LESSER,
    Comparator.NOT_EQUAL
];
export const amountComparatorLabel: Record<string, string> = {
    [Comparator.EQUAL]: 'Equal to',
    [Comparator.GREATER]: 'Greater than',
    [Comparator.LESSER]: 'Lesser than',
    [Comparator.NOT_EQUAL]: 'Not Equal to'
};
